import { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, IconButton, Typography } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DoneAllIcon from '@mui/icons-material/DoneAll';

import type { CartItem } from '../../cartSlice';
import {
  type CatalogCheckState,
  computeCatalogCheckStates,
  computeTreeCheckState,
} from '../../utils/catalogSelection';
import { CodeChip, TriStateCheckbox } from '../atoms';

import type { CspCatalogTreeNode } from '@/shared/types/userStandard';

interface CatalogSetTreeProps {
  nodes: CspCatalogTreeNode[];
  items: Record<string, CartItem>;
  onToggle: (node: CspCatalogTreeNode, state: CatalogCheckState) => void;
  onSelectAll: () => void;
  // When set, the matching row scrolls into view and is briefly highlighted (used by browse-into-set
  // navigation from a search result).
  highlightId?: string | null;
}

/**
 * Ids of every ancestor of `targetId` (the path from the roots down to, but excluding, the target),
 * or `null` when the target isn't in the tree. Used to reveal a browsed-to search result in an
 * otherwise-collapsed tree.
 */
export function collectAncestorIds(
  nodes: CspCatalogTreeNode[],
  targetId: string,
  trail: string[] = []
): string[] | null {
  for (const node of nodes) {
    if (node.id === targetId) return trail;
    const found = collectAncestorIds(node.children, targetId, [...trail, node.id]);
    if (found) return found;
  }
  return null;
}

// Branches to expand when the tree first loads: collapsed by default (parity with My Standards),
// except the path to a browsed-to search result so it lands in view.
function seedExpanded(nodes: CspCatalogTreeNode[], highlightId?: string | null): Set<string> {
  return new Set(highlightId ? (collectAncestorIds(nodes, highlightId) ?? []) : []);
}

interface RowProps {
  node: CspCatalogTreeNode;
  depth: number;
  states: Map<string, CatalogCheckState>;
  expanded: Set<string>;
  onToggle: (node: CspCatalogTreeNode, state: CatalogCheckState) => void;
  onToggleExpand: (id: string) => void;
  highlightId?: string | null;
}

function SetTreeNode({
  node,
  depth,
  states,
  expanded,
  onToggle,
  onToggleExpand,
  highlightId,
}: RowProps) {
  const hasChildren = node.children.length > 0;
  const isOpen = expanded.has(node.id);
  const state = states.get(node.id) ?? 'off';
  const isHighlighted = highlightId != null && node.id === highlightId;

  const rowRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (isHighlighted && rowRef.current) {
      rowRef.current.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
  }, [isHighlighted]);

  return (
    <>
      <Box
        ref={rowRef}
        onClick={() => hasChildren && onToggleExpand(node.id)}
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 0.5,
          pr: 1,
          pl: `${8 + depth * 18}px`,
          py: 0.5,
          cursor: hasChildren ? 'pointer' : 'default',
          borderRadius: 1,
          bgcolor: isHighlighted ? 'action.selected' : 'transparent',
          transition: 'background-color .3s',
          '&:hover': { bgcolor: hasChildren ? 'action.hover' : 'transparent' },
        }}
      >
        <IconButton
          size="small"
          onClick={(event) => {
            event.stopPropagation();
            if (hasChildren) onToggleExpand(node.id);
          }}
          sx={{ visibility: hasChildren ? 'visible' : 'hidden', p: 0.25, mt: 0.25 }}
          aria-label={isOpen ? 'Collapse' : 'Expand'}
        >
          <ChevronRightIcon
            fontSize="small"
            sx={{ transform: isOpen ? 'rotate(90deg)' : 'none', transition: 'transform .15s' }}
          />
        </IconButton>

        <Box sx={{ mt: 0.25 }}>
          <TriStateCheckbox
            state={state}
            onToggle={() => onToggle(node, state)}
            ariaLabel={`Select ${node.display_code}`}
          />
        </Box>

        <Box sx={{ flex: 1, minWidth: 0, pt: 0.25 }}>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
            {node.statement_label && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ textTransform: 'uppercase', letterSpacing: 0.4 }}
              >
                {node.statement_label}
              </Typography>
            )}
            <CodeChip code={node.display_code} />
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {node.description || node.display_code}
            </Typography>
          </Box>
        </Box>
      </Box>

      {hasChildren && isOpen && (
        <>
          {node.children.map((child) => (
            <SetTreeNode
              key={child.id}
              node={child}
              depth={depth + 1}
              states={states}
              expanded={expanded}
              onToggle={onToggle}
              onToggleExpand={onToggleExpand}
              highlightId={highlightId}
            />
          ))}
        </>
      )}
    </>
  );
}

/**
 * The recursive tri-state tree rows, without the panel chrome ("Select all" header / scroll box).
 * Reused both by the full set-tree panel and by inline search-result expansion. `baseDepth` shifts the
 * indentation so inline rows nest under their search row.
 */
export function CatalogTreeRows({
  nodes,
  states,
  expanded,
  onToggle,
  onToggleExpand,
  baseDepth = 0,
  highlightId,
}: {
  nodes: CspCatalogTreeNode[];
  states: Map<string, CatalogCheckState>;
  expanded: Set<string>;
  onToggle: (node: CspCatalogTreeNode, state: CatalogCheckState) => void;
  onToggleExpand: (id: string) => void;
  baseDepth?: number;
  highlightId?: string | null;
}) {
  return (
    <>
      {nodes.map((node) => (
        <SetTreeNode
          key={node.id}
          node={node}
          depth={baseDepth}
          states={states}
          expanded={expanded}
          onToggle={onToggle}
          onToggleExpand={onToggleExpand}
          highlightId={highlightId}
        />
      ))}
    </>
  );
}

export function CatalogSetTree({
  nodes,
  items,
  onToggle,
  onSelectAll,
  highlightId,
}: CatalogSetTreeProps) {
  const [expanded, setExpanded] = useState<Set<string>>(() => seedExpanded(nodes, highlightId));

  // Re-seed the collapsed state when a different set is loaded, or when navigating to a new highlight
  // within the same (cached) set — otherwise the second browsed-to result stays under a collapsed
  // ancestor and never scrolls into view. Only re-seed on a *non-null* highlight so the 4s fade back
  // to null doesn't collapse a tree the user is interacting with.
  const [seededFor, setSeededFor] = useState(nodes);
  const [seededHighlight, setSeededHighlight] = useState(highlightId);
  if (seededFor !== nodes || (highlightId != null && highlightId !== seededHighlight)) {
    setSeededFor(nodes);
    setSeededHighlight(highlightId);
    setExpanded(seedExpanded(nodes, highlightId));
  }

  const states = useMemo(() => computeCatalogCheckStates(nodes, items), [nodes, items]);
  // Disable "Select all" once every standard in the set is already selected.
  const allSelected = useMemo(() => computeTreeCheckState(nodes, items) === 'on', [nodes, items]);

  const toggleExpand = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 0.5, pt: 0.5 }}>
        <Button
          size="small"
          startIcon={<DoneAllIcon fontSize="small" />}
          onClick={onSelectAll}
          disabled={allSelected}
          sx={{ textTransform: 'none' }}
        >
          Select all
        </Button>
      </Box>

      <Box sx={{ py: 0.5, overflow: 'auto', flex: 1, minHeight: 0 }}>
        <CatalogTreeRows
          nodes={nodes}
          states={states}
          expanded={expanded}
          onToggle={onToggle}
          onToggleExpand={toggleExpand}
          highlightId={highlightId}
        />
      </Box>
    </Box>
  );
}

export default CatalogSetTree;
